import { ComponentType, FunctionComponent, MouseEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';

import { GridCellProps, GridDataStateChangeEvent } from '@progress/kendo-react-grid';

import { TagModel, TagsGridModel } from 'models';

import { NotificationsService } from 'core/notifications';
import {
  ActionListCell,
  Button,
  DEFAULT_DATA_TABLE_DATA_STATE,
  DEFAULT_PAGE_SIZES,
  DataTable,
  GridColumn,
  HeaderCell,
  Page,
  PageHeader,
  Toolbar,
  Window,
  WindowActionsBar,
  faPen,
} from 'core/ui';

import { useApiClient } from 'features/api';
import { useCurrentUser } from 'features/auth';

import { TagsEditService } from '../services';
import { TagsGridService } from '../services';
import { EditFormValues } from '../types';
import { TagsFieldTemplateForm } from './TagsFieldTemplateForm';
import { TagsGridHeaderCell } from './TagsGridHeaderCell';

const PageableSettings = {
  pageSizes: DEFAULT_PAGE_SIZES,
};

export const TagsFieldTemplateGrid: FunctionComponent = memo(() => {
  const apiClient = useApiClient();
  const { currentUser } = useCurrentUser(true);

  const [tags, setTags] = useState<TagsGridModel[]>([]);
  const [tagsTotal, setTagsTotal] = useState(0);
  const [dataState, setDataState] = useState(DEFAULT_DATA_TABLE_DATA_STATE);
  const [showWindow, setShowWindow] = useState(false);
  const [currentTag, setCurrentTag] = useState<TagModel | null>(null);
  const [parentTags, setParentTags] = useState<TagModel[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddNewClick = useCallback(() => {
    setCurrentTag(TagsEditService.createDefaultSource());
    setShowWindow(true);
  }, []);

  const handleEditClick = useCallback(
    (dataItem: TagsGridModel) => {
      (async () => {
        const tag = await apiClient.tagsClient.getTagsById(dataItem.id, 'msal-required');
        setCurrentTag(tag);
        setShowWindow(true);
      })();
    },
    [apiClient.tagsClient],
  );

  const handleWindowClose = useCallback(() => {
    setShowWindow(false);
    setCurrentTag(null);
  }, []);

  const handleFormSubmit = useCallback(
    async (values: EditFormValues) => {
      if (!currentTag) return;

      setIsSubmitting(true);
      try {
        const isNewTag = currentTag.id === 0;
        const newTag = TagsEditService.copyFormToModel(currentTag.id, values);

        // Get children values from the form
        const childrenValues = values.children || [];

        // Update the tag.children with form values
        newTag.children = (currentTag.children || []).map((child, index) => {
          if (index < childrenValues.length) {
            // Make sure all required properties are set
            return {
              ...child,
              name: childrenValues[index].name || '',
              description: child.description || '',
              tagFieldOption: child.tagFieldOption || null,
              active: typeof child.active === 'boolean' ? child.active : true,
              required: typeof child.required === 'boolean' ? child.required : false,
              multipleSelection: typeof child.multipleSelection === 'boolean' ? child.multipleSelection : false,
            };
          }
          return child;
        });

        // Ensure all required fields are set correctly
        if (!newTag.name || newTag.name.trim() === '') {
          throw new Error('Tag name is required');
        }

        // For new tags, make sure id is set to 0
        if (isNewTag) {
          newTag.id = 0;
        }

        // Call the API to save the tag
        const savedTag = await apiClient.tagsClient.addUpdateTags(newTag);

        if (savedTag) {
          if (isNewTag) {
            NotificationsService.displaySuccess(`Tag "${savedTag.name}" created successfully.`);
          } else {
            NotificationsService.displaySuccess(`Tag "${savedTag.name}" updated successfully.`);
          }

          // Refresh the grid data
          const queryResult = await apiClient.tagsClient.getAllTagsForKendoGrid(dataState);
          setTags(queryResult.data);
          setTagsTotal(queryResult.total);

          // Close the window
          setShowWindow(false);
          setCurrentTag(null);
        } else {
          // If no tag was returned, something went wrong
          throw new Error('Failed to save tag. No response received from server.');
        }
      } catch (error) {
        console.error('Error saving tag:', error);
        let errorMessage = 'An error occurred while saving the tag.';

        if (error instanceof Error) {
          errorMessage = error.message;
        }

        NotificationsService.displayError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [currentTag, apiClient.tagsClient, dataState],
  );

  // set the event for data state change
  const handleDataStateChange = useCallback((changeEvent: GridDataStateChangeEvent) => {
    setDataState(changeEvent.dataState);
  }, []);

  useEffect(() => {
    (async () => {
      const [queryResult, allTags] = await Promise.all([apiClient.tagsClient.getAllTagsForKendoGrid(dataState), apiClient.tagsClient.getAllTags()]);

      setTags(queryResult.data);
      setTagsTotal(queryResult.total);
      setParentTags(allTags);
    })();
  }, [dataState, apiClient.tagsClient]);

  const gridActions = useMemo(() => {
    return [
      {
        key: 'edit-form',
        title: 'Edit Form',
        icon: faPen,
        disabled: !currentUser.isSystemAdmin,
        onClick: (_: MouseEvent<HTMLButtonElement>, dataItem: TagsGridModel) => handleEditClick(dataItem),
      },
    ];
  }, [handleEditClick, currentUser.isSystemAdmin]);

  const { gridColumns, filterColumns } = useMemo(() => {
    return {
      gridColumns: TagsGridService.getGridColumns(),
      filterColumns: TagsGridService.getColumns(),
    };
  }, []);

  return (
    <Page>
      <PageHeader title="Tag Field Template" />
      <DataTable
        data={tags}
        filterable
        sortable
        pageable={PageableSettings}
        total={tagsTotal}
        onDataStateChange={handleDataStateChange}
        actions={gridActions}
        {...dataState}
      >
        <Toolbar onAddNewClick={handleAddNewClick} />
        <GridColumn
          title="Action"
          filterable={false}
          sortable={false}
          headerCell={HeaderCell}
          cell={ActionListCell as ComponentType<GridCellProps>}
          width="80px"
        />
        {gridColumns.map((column) => {
          return <GridColumn key={column.field} {...column} headerCell={TagsGridHeaderCell} cell={column.cell} filter={column.columnFilter} />;
        })}
      </DataTable>

      {showWindow && currentTag && (
        <Window modal title={currentTag.id === 0 ? 'New Tag Field Template' : 'Edit Tag Field Template'} onClose={handleWindowClose} initialWidth={725}>
          <TagsFieldTemplateForm tag={currentTag} parentTags={parentTags} onSubmit={handleFormSubmit} isSubmitting={isSubmitting} />
          <WindowActionsBar>
            <Button form="tag-form" type="submit" disabled={isSubmitting}>
              Save
            </Button>
          </WindowActionsBar>
        </Window>
      )}
    </Page>
  );
});

TagsFieldTemplateGrid.displayName = 'TagsFieldTemplateGrid';
